import React, { useState, useMemo } from "react";
import { LightbulbIcon } from "lucide-react";
import BookIcon from "src/assets/images/book.svg";
import SwitchIcon from "src/assets/images/switch.svg";

interface Visual {
  visual_name: string;
  reason: string;
}

interface JsonResponse {
  visuals: Visual[];
}

interface VisualConfirmationProps {
  json_response: JsonResponse | string[] | null;
}

const VisualConfirmation: React.FC<VisualConfirmationProps> = ({ json_response }) => {
  // Normalize json_response into an array of Visuals
  const visuals: Visual[] = useMemo(() => {
    if (!json_response) return [];

    // Case 1: Already an object
    if ((json_response as JsonResponse).visuals) {
      return (json_response as JsonResponse).visuals || [];
    }

    // Case 2: Array with string (your response)
    if (Array.isArray(json_response) && json_response.length > 0) {
      try {
        // Extract string like "visuals:[{...}]"
        const raw = json_response[0];

        if (typeof raw === "string" && raw.startsWith("visuals:")) {
          // Remove "visuals:" and try parsing JSON
          const parsed = JSON.parse(
            raw.replace("visuals:", "").replace(/(\w+):/g, '"$1":')
          );
          return parsed || [];
        }
      } catch (err) {
        console.error("Failed to parse json_response visuals:", err);
        return [];
      }
    }

    return [];
  }, [json_response]);

  const [open, setOpen] = useState<boolean>(true);

  return (
    <div
      className={`h-screen rounded-lg w-full overflow-hidden transition-all duration-300 ease-in-out ${
        open
          ? "flex-[0_0_100%] sm:flex-[0_0_75%] lg:flex-[0_0_460px] max-w-full lg:max-w-[460px]"
          : "flex-[0_0_100%] sm:flex-[0_0_215px] max-w-full sm:max-w-[215px]"
      }`}
    >
      <div className="flex flex-col h-full bg-opacity-50">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <img src={BookIcon} alt="Book Icon" className="w-3 h-3 object-contain" />
            <h2 className="text-lg font-semibold">Proposed Visuals</h2>
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="cursor-pointer rounded border border-[#DFE3E8] p-1 hover:bg-gray-100"
            aria-label="Toggle Panel"
          >
            <img src={SwitchIcon} alt="Toggle" className="w-3 h-3 object-contain" />
          </button>
        </div>

        {/* Main Content */}
        {open && (
          <div className="h-[55vh] overflow-y-auto pn_scroller flex flex-col">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Preview of Visuals
            </h3>

            <div className="overflow-x-auto pn_scroller">
              <table className="w-full border border-gray-200 rounded-lg">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="p-2 border-b text-base">Visual</th>
                    <th className="p-2 border-b text-base">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {visuals.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="p-2 border-b">{row.visual_name}</td>
                      <td className="p-2 border-b">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bottom Note */}
        {open && (
          <div className="flex items-start gap-2 text-xs text-gray-600 mt-4 px-1">
            <LightbulbIcon className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <span>
              Do these visuals reflect your expectations? Confirm or adjust before
              proceeding.
            </span>
          </div>
        )}

        {/* Footer Actions */}
        {/* {open && (
          <div className="mt-4 pt-3 border-t border-[#DBDEEB] flex flex-col items-end">
            <div className="flex items-center gap-2 mb-2">
              <button className="cursor-pointer rounded-lg px-2 py-1 border border-secondary-500 hover:bg-foundationOrange-100 text-[#373D3F]">
                Edit
              </button>
              <button className="cursor-pointer rounded-lg px-2 py-1 border border-secondary-500 hover:bg-foundationOrange-100 text-[#373D3F]">
                Replace/Remove
              </button>
            </div>
            <button className="cursor-pointer rounded-lg px-2 py-1 border bg-secondary-500 text-[#373D3F]">
              Accept & Continue
            </button>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default VisualConfirmation;
